import AuthSurface from '../components/AuthSurface';
import React, { useState, useEffect, useRef } from 'react';
import { authVisualPreview } from '../lib/authVisualPreview';
import LocalPreviewAccess from '../components/LocalPreviewAccess';
import { readRecentLoginAccounts, rememberLoginAccount, RECENT_LOGIN_ACCOUNTS_KEY } from '../lib/recentLoginAccounts';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../apiBase';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth, formatApiError } from '../context/AuthContext';
import { useLoginUsuario2810 } from '../lib/loginUsuario2810';
import { loginLandingPage } from '../lib/dashboardModes';
import { useTheme } from '../context/ThemeContext';
import { Loader2, Eye, EyeOff, ShieldCheck, ArrowRight, UserRound, X, ChevronLeft } from 'lucide-react';
import AuthCardHeader, { AuthThemeSlot } from '../components/AuthCardHeader';
import { BrandLogoImage } from '../components/BrandMark';
import LoginDragao3110 from '../components/LoginDragao3110'; // LOGIN_DRAGAO_3110
import { useAuraxFlags } from '../lib/auraxFlags';
import { applyAppearanceColors } from '../lib/brandPrimary';
import { applyBrowserBrand } from '../lib/applyBrowserBrand';
import { resolveBrandLogoUrl } from '../lib/brandLogo';
import GoogleSignInButton, { AuthDivider } from '../components/GoogleSignInButton';

const Login = () => {
  const [email, setEmail] = useState('');
  const loginUsuario2810 = useLoginUsuario2810(); // LOGIN_USUARIO_2810
  const auraxFlags3110 = useAuraxFlags(); // LOGIN_DRAGAO_3110
  const [recentAccounts,setRecentAccounts] = useState(readRecentLoginAccounts);
  const [showAccountList,setShowAccountList] = useState(() => readRecentLoginAccounts().length > 0);
  const [rememberAccount,setRememberAccount] = useState(false);
  const pendingAccountRef = useRef(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const mfaOriginRef = useRef('password');
  const mfaFocusRef = useRef(null);
  const [branding, setBranding] = useState({
    brand_name: 'Dragon Tech',
    logo_url: '',
    logo_url_light: '',
    logo_url_dark: '',
  });
  const [officeColors, setOfficeColors] = useState({ brand_accent: '', primary_color: '' });
  const { login, loginWithGoogle, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const { resolved, applyBranding } = useTheme();
  const authVisual = authVisualPreview();

  const siteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();

  useEffect(() => {
    if (mfaStep || !mfaFocusRef.current) return;
    const target = document.querySelector(`[data-testid="${mfaFocusRef.current}"]`);
    mfaFocusRef.current = null;
    target?.focus();
  }, [mfaStep,showAccountList]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/public/branding`)
      .then(({ data }) => {
        if (!cancelled && data) {
          setBranding({
            brand_name: data.brand_name || 'Dragon Tech',
            logo_url: data.logo_url || '',
            logo_url_light: data.logo_url_light || data.logo_url || '',
            logo_url_dark: data.logo_url_dark || data.logo_url || '',
          });
          applyBrowserBrand({
            browser_title: data.browser_title || '',
            brand_name: data.brand_name || 'Dragon Tech',
            favicon_url: data.favicon_url || '',
          });
          const colors = {
            brand_accent: data.brand_accent || '',
            primary_color: data.primary_color || '',
          };
          setOfficeColors(colors);
          // Tema: respeita toggle do utilizador. Cores: sempre as do escritório.
          applyBranding({
            brand_theme: data.brand_theme || undefined,
            brand_accent: data.brand_accent || '',
            forceTheme: false,
            forceAccent: true,
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [applyBranding]);

  // Páginas públicas: força a cor definida no Office (rosa, azul, etc.)
  useEffect(() => {
    if (!officeColors.brand_accent && !officeColors.primary_color) return;
    applyAppearanceColors(officeColors, resolved === 'dark' ? 'dark' : 'light', { force: true });
  }, [officeColors, resolved]);

  const mapLoginError = (err) => {
    const body = err.response?.data;
    const detail =
      body?.detail ?? body?.message ?? (typeof body === 'string' && body.trim() ? body : null);
    const isHtml =
      err?.isHtmlResponse === true ||
      (typeof detail === 'string' &&
        (detail.includes('<!DOCTYPE') || detail.toLowerCase().includes('<html'))) ||
      (typeof err.message === 'string' && err.message.toLowerCase().includes('html'));

    if (isHtml) return 'Servidor indisponível. Tente novamente em instantes.';
    if (detail != null && detail !== '') return formatApiError(detail);
    if (
      err.code === 'ERR_NETWORK' ||
      err.message === 'Network Error' ||
      (err.response && err.response.status === 0)
    ) {
      return 'Sem conexão com o servidor.';
    }
    if (err.response?.status === 404) {
      return 'Conta não encontrada. Cadastre-se pelo link de indicação ou use e-mail e senha.';
    }
    if (err.response?.status === 401 || err.response?.status === 403) {
      const detail = formatApiError(err.response?.data?.detail) || '';
      if (detail && !/^e-?mail ou senha incorretos\.?$/i.test(detail.trim())) {
        return detail;
      }
      return mfaStep ? 'Código do Authenticator inválido.' : (loginUsuario2810 ? 'Usuário ou senha incorretos.' : 'E-mail ou senha incorretos.');
    }
    return err.message || 'Não foi possível entrar.';
  };

  const mfaTerminalError = (err) => {
    const detail = formatApiError(err?.response?.data?.detail) || '';
    const normalized = detail.toLocaleLowerCase('pt-BR');
    if (normalized.includes('senha foi alterada')) return detail;
    if (normalized.includes('sessão mfa expirada') || normalized.includes('sessão mfa inválida')) {
      return 'A verificação expirou. Um novo código do Authenticator não renova esta tentativa. Entre novamente.';
    }
    return '';
  };

  const leaveMfa = (message = '') => {
    mfaFocusRef.current = mfaOriginRef.current === 'google' ? 'login-email' : 'login-password';
    pendingAccountRef.current=null;
    setMfaStep(false);
    setMfaCode('');
    setPassword('');
    setShowPassword(false);
    setTurnstileToken(null);
    setError(message);
  };

  useEffect(() => {
    const changed = event => { if (!event.key || event.key === RECENT_LOGIN_ACCOUNTS_KEY) setRecentAccounts(readRecentLoginAccounts()); };
    window.addEventListener('storage',changed);
    return () => window.removeEventListener('storage',changed);
  }, []);

  const finishAccount = data => {
    const pending=pendingAccountRef.current;
    if (pending) setRecentAccounts(rememberLoginAccount(pending.identifier || data?.email,pending.remember));
    pendingAccountRef.current=null;
  };
  const chooseAccount = account => {
    setEmail(account.identifier);setPassword('');setShowPassword(false);setRememberAccount(true);setError('');setTurnstileToken(null);
    mfaFocusRef.current='login-password';setShowAccountList(false);
  };
  const otherAccount = () => {
    setEmail('');setPassword('');setShowPassword(false);setRememberAccount(false);setError('');setTurnstileToken(null);
    mfaFocusRef.current='login-email';setShowAccountList(false);
  };
  const forgetAccount = account => {
    const rows=rememberLoginAccount(account.identifier,false);setRecentAccounts(rows);
    if (!rows.length) otherAccount();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (siteKey && !turnstileToken) {
      setError('Confirme a verificação de segurança antes de entrar.');
      return;
    }
    setLoading(true);
    try {
      pendingAccountRef.current={identifier:email.trim(),remember:rememberAccount};
      const data = await login(email, password, turnstileToken || undefined);
      if (data?.mfa_required) {
        mfaOriginRef.current = 'password';
        setMfaStep(true);
        setMfaCode('');
        setPassword('');
        setShowPassword(false);
        return;
      }
      finishAccount(data);
      navigate(loginLandingPage(data?.role));
    } catch (err) {
      setError(mapLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      pendingAccountRef.current={identifier:'',remember:rememberAccount};
      const data = await loginWithGoogle(credential);
      if (data?.mfa_required) {
        mfaOriginRef.current = 'google';
        setMfaStep(true);
        setMfaCode('');
        return;
      }
      finishAccount(data);
      navigate(loginLandingPage(data?.role));
    } catch (err) {
      setError(mapLoginError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    const code = (mfaCode || '').replace(/\D/g, '');
    if (code.length !== 6) {
      setError('Digite o código de 6 dígitos do Google Authenticator.');
      return;
    }
    setLoading(true);
    try {
      const session = await verifyMfa(code);
      finishAccount(session);
      navigate(loginLandingPage(session?.role));
    } catch (err) {
      const terminalMessage = mfaTerminalError(err);
      if (terminalMessage) leaveMfa(terminalMessage);
      else setError(mapLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const brandName = (branding.brand_name || 'Dragon Tech').trim();
  const isAttendantPortal = window.location.hostname.toLowerCase().startsWith('atendimento.');
  const initial = brandName.charAt(0).toUpperCase() || 'A';
  const logoSrc = resolveBrandLogoUrl(branding, resolved === 'dark' ? 'dark' : 'light');

  const dragaoOn3110 = auraxFlags3110?.login_dragao_3110 !== false && (()=>{ try { return localStorage.getItem("aurax_login_dragao_3110") !== "0"; } catch { return true; } })(); // LOGIN_DRAGAO_3110 (private/LOGIN_DRAGAO_3110_OFF)
  return (
    <AuthSurface as="main" brandName={brandName} className={`login-aurax login-aurax--office-entry${dragaoOn3110 ? ' ldg-on' : ''}`} data-theme={resolved} data-auth-visual={authVisual} data-testid="login-page" data-auth-step={mfaStep ? 'mfa' : 'login'}>

      {dragaoOn3110 ? <LoginDragao3110 /> : null}
      <section className="login-aurax__panel" data-testid="login-card">
        <AuthThemeSlot visual={authVisual} />

        <div className="login-aurax__card">
          <AuthCardHeader visual={authVisual}>
            <div className="login-aurax__card-brand">
              {logoSrc ? (
                <BrandLogoImage
                  key={logoSrc}
                  src={logoSrc}
                  alt={brandName}
                  className="login-aurax__logo-free"
                />
              ) : (
                <span className="login-aurax__mark login-aurax__mark--lg" aria-hidden>
                  {initial}
                </span>
              )}
              <span className="login-aurax__mobile-kicker">
                <ShieldCheck size={12} strokeWidth={2.2} />
                Acesso seguro · {brandName}
              </span>
            </div>
          </AuthCardHeader>

          <div className="login-aurax__intro">
            <h1 className="login-aurax__title">
              {mfaStep ? 'Confirme sua identidade' : isAttendantPortal ? 'Portal de atendimento' : 'Acesse seu Office'}
            </h1>
            <p className="login-aurax__subtitle">
              {mfaStep
                ? 'Abra o Google Authenticator e digite o código de 6 dígitos desta conta.'
                : isAttendantPortal
                  ? 'Use a conta fornecida pelo administrador para iniciar seu turno com segurança.'
                  : 'Entre com sua conta para administrar o escritório e acompanhar a operação.'}
            </p>
          </div>

          <LocalPreviewAccess />

          {!mfaStep && showAccountList && recentAccounts.length ? (
            <section className="login-aurax__accounts" aria-label="Contas recentes neste dispositivo">
              <ul>{recentAccounts.map((account,index) => <li key={account.identifier.toLocaleLowerCase()}>
                <span className="login-aurax__account-name" title={account.identifier}>{account.identifier}</span>
                {index === 0 && <span className="login-aurax__account-last">Último uso</span>}
                <button type="button" className="login-aurax__account" onClick={() => chooseAccount(account)} aria-label={`Continuar com ${account.identifier}`}>
                  <UserRound aria-hidden size={18} /><span>Continuar</span>
                  <ArrowRight aria-hidden size={16} />
                </button>
                <button type="button" className="login-aurax__account-remove" aria-label={`Remover ${account.identifier} deste dispositivo`} onClick={() => forgetAccount(account)}><X aria-hidden size={17} /></button>
              </li>)}</ul>
              <div className="login-aurax__account-divider" aria-hidden><span>ou</span></div>
              <button type="button" className="login-aurax__other-account" onClick={otherAccount}>Fazer login com outra conta</button>
            </section>
          ) : mfaStep ? (
            <form onSubmit={handleMfaSubmit} className="login-aurax__form" data-testid="login-mfa-form">
              {error ? (
                <div className="login-aurax__error" data-testid="login-error" role="alert">
                  {error}
                </div>
              ) : null}
              <div className="login-aurax__field">
                <label htmlFor="mfa-code" className="login-aurax__label">
                  Código do Authenticator
                </label>
                <input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="login-aurax__input login-aurax__input--otp"
                  required
                  autoFocus
                  data-testid="login-mfa-code"
                />
              </div>
              <button type="submit" className="login-aurax__submit" disabled={loading} data-testid="login-mfa-submit">
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    Confirmar e entrar
                    <ArrowRight size={18} strokeWidth={2} />
                  </>
                )}
              </button>
              <button
                type="button"
                className="login-aurax__ghost"
                disabled={loading}
                onClick={() => leaveMfa()}
                data-testid="login-mfa-back"
              >
                Voltar ao e-mail e senha
              </button>
            </form>
          ) : (
            <>
              <div className="login-aurax__google-block">
                <GoogleSignInButton
                  text="signin"
                  disabled={loading}
                  onCredential={handleGoogleCredential}
                  labelFallback="Entrar com Google"
                />
                <AuthDivider label="ou entre com e-mail" />
              </div>
            {recentAccounts.length > 0 && <button type="button" className="login-aurax__back" onClick={() => { setPassword('');setShowPassword(false);setError('');setTurnstileToken(null);setShowAccountList(true); }}><ChevronLeft aria-hidden size={16} />Contas recentes</button>}
            <form onSubmit={handleSubmit} className="login-aurax__form" data-testid="login-form">
              {error ? (
                <div className="login-aurax__error" data-testid="login-error" role="alert">
                  {error}
                </div>
              ) : null}
              <div className="login-aurax__field">
                <label htmlFor="email" className="login-aurax__label">
                  {loginUsuario2810 ? 'Usuário' : 'E-mail'}
                </label>
                <input
                  id="email"
                  type="text"
                  inputMode={loginUsuario2810 ? 'text' : 'email'}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  placeholder={loginUsuario2810 ? 'Usuário' /* LOGIN_LIMPO_3110 antes: Usuário (ex.: painel01) */ : 'E-mail'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-aurax__input"
                  required
                  data-testid="login-email"
                />
              </div>
              <div className="login-aurax__field">
                <div className="login-aurax__label-row">
                  <label htmlFor="password" className="login-aurax__label">
                    Senha
                  </label>
                </div>
                <div className="login-aurax__input-wrap">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-aurax__input"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    className="login-aurax__toggle-pw"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} strokeWidth={1.75} /> : <Eye size={18} strokeWidth={1.75} />}
                  </button>
                </div>
                <Link to="/forgot-password" className="login-aurax__link login-aurax__forgot" data-testid="forgot-password-link">
                  Esqueceu a senha?
                </Link>
              </div>
              <label className="login-aurax__remember"><input type="checkbox" checked={rememberAccount} disabled={loading} onChange={e => setRememberAccount(e.target.checked)} data-testid="login-remember-account" /><span>Lembrar minha conta neste dispositivo</span></label>
              {siteKey ? (
                <div className="login-aurax__captcha">
                  <Turnstile
                    siteKey={siteKey}
                    onSuccess={setTurnstileToken}
                    options={{ theme: resolved === 'dark' ? 'dark' : 'light', size: 'compact' }}
                  />
                </div>
              ) : null}
              <button type="submit" className="login-aurax__submit" disabled={loading} data-testid="login-submit">
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Aguarde...
                  </>
                ) : (
                  <>
                    Continuar
                    <ArrowRight size={18} strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
            </>
          )}

          <div className="login-aurax__legal">
            <p className="login-aurax__footer">
              <ShieldCheck size={15} strokeWidth={1.9} className="login-aurax__footer-icon" aria-hidden />
              Acesso protegido{branding.login_show_brand === false ? '' : ` · ${brandName}`}
            </p>
            <p className="login-aurax__terms" style={{ display: 'none' }} /* LOGIN_LIMPO_3110 */>
              Use sua conta do Office. Para acessar um plano, entre na área do cliente.
            </p>
          </div>
        </div>
      </section>
    </AuthSurface>
  );
};

export default Login;
